"use strict";

import { Application } from "../main/application.js";
import { Screen } from "../systems/core/screen.js";
import { UIObjectCreator } from "./controllers/uiObjectCreator.js";
import { UITransformHandler } from "./controllers/uiTransformHandler.js";
import { Vector2 } from "../math/vectors.js";

export class UIContainer extends PIXI.Container {
    constructor(config) {
        super();

        this.name = config.name;

        // Create Parenting
        UIObjectCreator.createParenting(this, config);

        // Create Rect
        UIObjectCreator.createRect(this, config);

        UIObjectCreator.setAspectRatioBehaviour(this, config);

        // Set Rotation Angle
        UIObjectCreator.setRotationAngle(this, config);
        // Set Visibility
        UIObjectCreator.setVisibility(this, config);
        // Set Renderability
        UIObjectCreator.setRenderability(this, config);
        // Set Interactibility
        UIObjectCreator.setInteractibility(this, config);

        UIObjectCreator.setResolutionListeners(this, true, config);
        UIObjectCreator.setOrientationListeners(this, true, config);

        UIObjectCreator.addGenericTransformations(this, config);

        config.onInitialize?.bind(this)(config);

        if (config.objects !== undefined && Array.isArray(config.objects) &&
            config.objects.length > 0) {
            this.objects = config.objects;
            for (let i = 0; i < config.objects.length; i++) {
                this.objects[i].setParent(this);
            }
        }

        this.updateNow();
    }

    get widthUI() {
        return this.parentContainer.transformFromUI(this._width);
    }
    get heightUI() {
        return this.parentContainer.transformFromUI(this._height);
    }

    get lossyScaleX() {
        return this.scale.x * (this.parentContainer?.lossyScaleX ?? 1);
    }
    get lossyScaleY() {
        return this.scale.y * (this.parentContainer?.lossyScaleY ?? 1);
    }
    get lossyScale() {
        return this.lossyScaleY;
    }

    get preferredBoundX() {
        return this._width * this.lossyScaleX;
    }
    get preferredBoundY() {
        return this._height * this.lossyScaleY;
    }
    get preferredBounds() {
        return { width: this.preferredBoundX, height: this.preferredBoundY };
    }

    dispose() {
        this.parentContainer?.removeChild(this);
        this.destroy();
    }

    // Set Parent
    setParent(newParent) {
        this.parentContainer = newParent;
        this.parentContainer.addChild(this);
        this.updateNow();
    }

    setRect(newRect) {
        UITransformHandler.setRect(this, newRect);
        this.updateNow();
    }

    getPosition() {
        return { x: this.x, y: this.y };
    }
    setPosition(x,y) {
        this.x = x.x ?? x;
        this.y = x.y ?? y;
    }
    getRealWorldPosition() {
        this.parentContainer.updateTransform();
        return this.parentContainer.toGlobal(this.getPosition());
    }
    getWorldPosition(invertY) {
        let position = this.getRealWorldPosition();
        position.x -= Application.main.worldCenterX;
        position.y -= Application.main.worldCenterY;
        if (invertY) {
            position.y = -position.y;
        }
        return Vector2.divide(position, Screen.main.stageScale);
    }

    transformToLocal(position) {
        return this.toLocal(position);
    }

    _refreshTransform() {
        if (this.parentContainer === undefined) return;

        UITransformHandler.updateContainerTransform(this, {...this.parentContainer.preferredBounds});

        switch(this.preserveAspectRatio.mode) {
            case "widthControlsHeight":
                UITransformHandler.ARWidthControlsHeight(this);
                break;
            case "heightControlsWidth":
                UITransformHandler.ARHeightControlsWidth(this);
                break;
            case "fitInParent":
                UITransformHandler.ARFitInParent(this);
                break;
        }
        
        this.calculatedRect.size.width = this.transformFromUI(this._width)
        this.calculatedRect.size.height = this.transformFromUI(this._height);
    }

    onResolutionChanged() {
        this.scale.x = 1;
        this.scale.y = 1;

        this._onResolutionWillChangeCallback?.(this);

        this._refreshTransform();

        this._onResolutionChangedCallback?.(this);

        for (let i = 0; i < this.children.length; i++) {
            this.children[i].onResolutionChanged();
        }

        this._onResolutionChangedAfterCallback?.(this);

        this.scale.x = this.rect.scale.x;
        this.scale.y = this.rect.scale.y;
    }

    onOrientationChanged() {
        this._onOrientationWillChangeCallback?.(this);

        for (let i = 0; i < this.children.length; i++) {
            this.children[i].onOrientationChanged();
        }

        this._onOrientationChangedCallback?.(this);
    }

    updateNow() {
        this.onResolutionChanged();
    }
}